<template>
	<div class="data-sharing" ref="zoneRef">
		<div class="data-sharing__kpis">
			<Kpi v-for="card in kpiCards" :key="card.key" class="data-sharing__kpi" :class="`data-sharing__kpi--${card.key}`" :title="card.title" display-mode="card" :show-difference="true" :static-data="card.data" />
		</div>

		<div class="data-sharing__panel data-sharing__controls">
			<div class="data-sharing__dates">
				<RangeDatePickerControl v-model="dateRange" :label="t('Period')" :presets="presets">
					<template #footer>
						<div class="data-sharing__offline-hint text-caption text-medium-emphasis" v-if="isOffline">
							<v-icon>cloud_off</v-icon>
							{{ t('OfflineDataLimited') }}
						</div>
					</template>
				</RangeDatePickerControl>
			</div>
			<v-btn class="data-sharing__export" color="secondary" variant="tonal" rounded="xl" prepend-icon="download" @click="onExport">{{ t('Export') }}</v-btn>
		</div>

		<div class="data-sharing__panel data-sharing__detail">
			<div class="data-sharing__detail-header">
				<v-icon>inventory_2</v-icon>
				<span class="data-sharing__detail-title">{{ t('ProductDetail') }}</span>
				<span class="data-sharing__detail-count" v-if="!loading && tableRows.length">· {{ displayedRows.length }} {{ t('Products') }}</span>

				<v-spacer></v-spacer>
				<v-text-field class="data-sharing__search" v-model="searchText" :label="t('Search')" prepend-inner-icon="search" variant="outlined" density="compact" clearable hide-details />
			</div>

			<div class="data-sharing__columns-panel">
				<span class="text-caption text-uppercase text-medium-emphasis">{{ t('Columns') }}</span>
				<QuickColumn :columns="measureColumns" v-model="visibleMeasures" :default-visible-columns="defaultVisibleMeasures" />
			</div>

			<div class="data-sharing__legend text-body-2 text-medium-emphasis" v-if="!loading && tableRows.length">
				<span class="data-sharing__legend-dot data-sharing__legend-dot--p1"></span>
				{{ t('P1') }} : {{ formatDate(resolvedP1) }}
				<span class="data-sharing__legend-dot data-sharing__legend-dot--p2"></span>
				{{ t('P2') }} : {{ formatDate(resolvedP2) }}
				<span class="font-italic text-disabled ml-2">{{ t('Gap') }} = {{ t('EvolutionP1P2') }}</span>
			</div>

			<div class="d-flex justify-center pa-8" v-if="loading">
				<v-progress-circular indeterminate color="primary" />
			</div>
			<div class="text-center pa-8 text-disabled" v-else-if="!tableRows.length">{{ t('NoData') }}</div>
			<template v-else>
				<TouchCardData v-if="isCardView" :row-data="displayedRows" :columns="touchColumns" metric-layout="rows" title-field="label" :subtitle-fields="['reference', 'ean']" sort-storage-key="datasharing" />
				<AgGrid v-else ref="gridRef" class="data-sharing__grid" :row-data="tableRows" :pinned-bottom-row-data="totalsRow" :col-defs="columnDefs" :get-row-id="getRowId" :enable-charts="true" :cell-selection="true" :rowGroupPanelShow="'never'" />
			</template>
		</div>
	</div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick } from 'vue';
import { buildPresets, DATA_SHARING_PRESET_KEYS } from '@/utils/domain/periodPresets';
import { iframeDataBridge } from '@/iframe/iframeDataBridge';
import { useNetworkStatus } from '@/composables/useNetworkStatus';
import { useTranslator } from '@/composables/useTranslator';
import { getProductDeliveryLocationDiffusions } from '@/api/assort/productDeliveryLocationDiffusion';
import { log } from '@/utils/core/vueLogger.js';
import Kpi from '@/components/kpi/Kpi.vue';
import AgGrid from '@/components/agGrid/AgGrid.vue';
import TouchCardData from '@/components/agGrid/TouchCardData.vue';
import QuickColumn from '@/components/agGrid/QuickColumn.vue';
import RangeDatePickerControl from '@/components/controls/RangeDatePickerControl.vue';

const t = useTranslator();
const zoneRef = ref(null);
const gridRef = ref(null);

const deliveryLocationId = ref(null);
const rawData = ref([]);
const products = ref([]);
const productMap = computed(() => Object.fromEntries((products.value || []).map((p) => [String(p.id), p])));
const loading = ref(false);

const dateRange = ref([]);
const visibleMeasures = ref([]);
const searchText = ref('');

const rangeStart = computed(() => dateRange.value?.[0] ?? null);
const rangeEnd = computed(() => dateRange.value?.[dateRange.value.length - 1] ?? null);

watch(dateRange, (val) => {
	if (val.length === 2) {
		nextTick(() => gridRef.value?.getGridApi()?.setGridOption('rowData', tableRows.value));
	}
});

const presets = computed(() => buildPresets(DATA_SHARING_PRESET_KEYS, t));

function dayValue(date) {
	if (!date) return null;
	const d = new Date(date);
	d.setHours(0, 0, 0, 0);
	return d.getTime();
}

const CARD_BREAKPOINT = 600;
const viewportWidth = ref(window.innerWidth);
const isCardView = computed(() => viewportWidth.value < CARD_BREAKPOINT);
const onResize = () => {
	viewportWidth.value = window.innerWidth;
};

let _mountTarget = null;

const iframeWin = iframeDataBridge.getIframeWindow();
const currency = iframeWin?.externis?.sales?.salesHelper?.getCurrency?.() || '€';

const MEASURES = computed(() => [
	{ key: 'volume', label: t('SellOutVolume'), decimals: 0, suffix: '' },
	{ key: 'value', label: t('Valeur'), decimals: 0, suffix: currency },
	{ key: 'vmm', label: t('VMM'), decimals: 1, suffix: '' },
	{ key: 'vmh', label: t('VMH'), decimals: 1, suffix: '' },
]);

const defaultVisibleMeasures = ['vmh'];
const measureColumns = computed(() => MEASURES.value.map((m) => ({ field: m.key, label: m.label })));
const activeMeasures = computed(() => MEASURES.value.filter((m) => visibleMeasures.value.includes(m.key)));

const availablePeriods = computed(() => {
	const dates = [...new Set((rawData.value || []).map((r) => r.date).filter(Boolean))];
	return dates.sort((a, b) => new Date(a) - new Date(b));
});

const { isOffline } = useNetworkStatus();

const periodsInRange = computed(() => {
	if (!rangeStart.value || !rangeEnd.value) return [];
	const start = dayValue(rangeStart.value);
	const end = dayValue(rangeEnd.value);
	return availablePeriods.value.filter((period) => {
		const value = dayValue(period);
		return value >= start && value <= end;
	});
});

function nearestPeriod(inputDate, periods) {
	if (!inputDate || !periods.length) return null;
	const target = new Date(inputDate).getTime();
	let best = periods[0];
	let bestDiff = Infinity;
	for (const period of periods) {
		const diff = Math.abs(new Date(period).getTime() - target);
		if (diff < bestDiff) {
			bestDiff = diff;
			best = period;
		}
	}
	return best;
}

const resolvedP1 = computed(() => nearestPeriod(rangeStart.value, periodsInRange.value));
const resolvedP2 = computed(() => nearestPeriod(rangeEnd.value, periodsInRange.value));

function aggregateByProduct(periodDate) {
	const byProduct = {};
	if (!periodDate) return byProduct;
	(rawData.value || []).forEach((r) => {
		if (r.date !== periodDate) return;
		const id = r.productId;
		if (!byProduct[id]) {
			const p = productMap.value[String(id)] || {};
			byProduct[id] = { productId: id, ean: p.ean || '', reference: p.reference || '', label: p.name || '', volume: 0, value: 0, _vmmSum: 0, _vmhSum: 0, _count: 0 };
		}
		const agg = byProduct[id];
		agg.volume += r.saleVolume || 0;
		agg.value += r.saleValue || 0;
		agg._vmmSum += r.vmm || 0;
		agg._vmhSum += r.vmh || 0;
		agg._count += 1;
	});
	Object.values(byProduct).forEach((agg) => {
		agg.vmm = agg._count ? agg._vmmSum / agg._count : 0;
		agg.vmh = agg._count ? agg._vmhSum / agg._count : 0;
	});
	return byProduct;
}

const p1ByProduct = computed(() => aggregateByProduct(resolvedP1.value));
const p2ByProduct = computed(() => aggregateByProduct(resolvedP2.value));

const tableRows = computed(() => {
	const p1 = p1ByProduct.value;
	const p2 = p2ByProduct.value;
	const ids = new Set([...Object.keys(p1), ...Object.keys(p2)]);

	return [...ids].map((id) => {
		const a = p2[id] || p1[id];
		const row = { id, productId: id, ean: a.ean, reference: a.reference, label: a.label };
		MEASURES.value.forEach((m) => {
			const v1 = round(p1[id]?.[m.key] ?? 0, m.decimals);
			const v2 = round(p2[id]?.[m.key] ?? 0, m.decimals);
			row[`${m.key}P1`] = v1;
			row[`${m.key}P2`] = v2;
			row[`${m.key}Evo`] = evo(v2, v1);
		});
		return row;
	});
});

const displayedRows = computed(() => {
	const query = searchText.value?.trim().toLowerCase();
	if (!query) return tableRows.value;
	return tableRows.value.filter((row) =>
		[row.ean, row.reference, row.label].some((field) =>
			String(field || '')
				.toLowerCase()
				.includes(query)
		)
	);
});

// --- KPI cards --------------------------------------------------------------

const totals = computed(() => {
	const p1 = Object.values(p1ByProduct.value);
	const p2 = Object.values(p2ByProduct.value);
	const sum = (rows, key) => rows.reduce((s, r) => s + (r[key] || 0), 0);
	const avg = (rows, key) => (rows.length ? sum(rows, key) / rows.length : 0);
	return {
		volumeP1: sum(p1, 'volume'),
		volumeP2: sum(p2, 'volume'),
		valueP1: sum(p1, 'value'),
		valueP2: sum(p2, 'value'),
		vmmP1: avg(p1, 'vmm'),
		vmmP2: avg(p2, 'vmm'),
		vmhP1: avg(p1, 'vmh'),
		vmhP2: avg(p2, 'vmh'),
	};
});

const progression = computed(() => {
	const rows = tableRows.value;
	const progressed = rows.filter((r) => (r.volumeP2 || 0) > (r.volumeP1 || 0)).length;
	const declined = rows.filter((r) => (r.volumeP2 || 0) < (r.volumeP1 || 0)).length;
	const gained = rows.filter((r) => !r.volumeP1 && (r.volumeP2 || 0) > 0).length;
	return { progressed, declined, gained, total: rows.length };
});

function buildKpiData(currentValue, compareValue, decimals, unit) {
	const diff = evo(currentValue, compareValue);
	return {
		formattedValue: formatNumber(currentValue, decimals),
		unit,
		formattedDifference: diff != null ? formatPct(diff) : null,
		difference: diff,
		classColor: diff == null ? '' : diff >= 0 ? 'positive' : 'negative',
		differenceLabel: t('vsP1'),
	};
}

function buildProgressionData() {
	const { progressed, declined, gained, total } = progression.value;
	return {
		formattedValue: `${progressed} / ${total}`,
		unit: '',
		formattedDifference: `${declined}`,
		difference: declined > 0 ? -declined : 0,
		classColor: declined > 0 ? 'negative' : 'positive',
		differenceLabel: t('InDecline'),
		formattedDifference2: `${gained}`,
		difference2: gained > 0 ? gained : 0,
		classColor2: gained > 0 ? 'positive' : '',
		differenceLabel2: t('CodesGained'),
	};
}

const kpiCards = computed(() => [
	{ key: 'volume', title: t('VolumeTotal(P2)'), data: buildKpiData(totals.value.volumeP2, totals.value.volumeP1, 0, t('UnitsShort')) },
	{ key: 'value', title: t('TurnoverCA(P2)'), data: buildKpiData(totals.value.valueP2, totals.value.valueP1, 0, currency) },
	{ key: 'vmm', title: t('AverageVMM(P2)'), data: buildKpiData(totals.value.vmmP2, totals.value.vmmP1, 0, t('UnitsPerMonth')) },
	{ key: 'progression', title: t('RefsInProgression'), data: buildProgressionData() },
]);

const columnDefs = computed(() => {
	const identity = [
		{
			headerName: t('Product'),
			field: 'product',
			colId: 'product',
			children: [
				{ headerName: t('Ean'), field: 'ean', colId: 'ean', minWidth: 130 },
				{ headerName: t('Reference'), field: 'reference', colId: 'reference', minWidth: 110 },
				{ headerName: t('Label'), field: 'label', colId: 'label', minWidth: 200 },
			],
		},
	];

	const measureGroups = activeMeasures.value.map((m) => ({
		headerName: m.suffix ? `${m.label} (${m.suffix})` : m.label,
		headerClass: `data-sharing__grid-header--${m.key}`,
		children: [
			{ headerName: t('P1'), colId: `${m.key}_p1`, field: `${m.key}P1`, cellDataType: 'number', valueFormatter: (p) => formatMeasure(p.value, m.decimals, m.suffix) },
			{ headerName: t('P2'), colId: `${m.key}_p2`, field: `${m.key}P2`, cellDataType: 'number', valueFormatter: (p) => formatMeasure(p.value, m.decimals, m.suffix) },
			{
				headerName: t('Gap'),
				colId: `${m.key}_evo`,
				field: `${m.key}Evo`,
				cellDataType: 'number',
				valueFormatter: (p) => (p.value == null ? '' : formatPct(p.value)),
				cellStyle: (p) => (p.value == null ? null : p.value >= 0 ? { color: '#2e7d32', fontWeight: 600 } : { color: '#c62828', fontWeight: 600 }),
			},
		],
	}));

	return [...identity, ...measureGroups];
});

const totalsRow = computed(() => {
	if (!tableRows.value.length) return null;
	const row = { ean: '', reference: '', label: t('Total') };
	MEASURES.value.forEach((m) => {
		const v1 = round(totals.value[`${m.key}P1`] ?? 0, m.decimals);
		const v2 = round(totals.value[`${m.key}P2`] ?? 0, m.decimals);
		row[`${m.key}P1`] = v1;
		row[`${m.key}P2`] = v2;
		row[`${m.key}Evo`] = evo(v2, v1);
	});
	return [row];
});

const touchColumns = computed(() =>
	activeMeasures.value.map((m) => ({
		field: `${m.key}P2`,
		fromField: `${m.key}P1`,
		evoField: `${m.key}Evo`,
		label: m.label,
		suffix: m.suffix,
		decimals: m.decimals,
	}))
);

function getRowId(params) {
	return String(params.data.productId);
}

function applyQuickFilter() {
	if (isCardView.value) return;
	const api = gridRef.value?.getGridApi?.();
	if (api && typeof api.setGridOption === 'function') {
		api.setGridOption('quickFilterText', searchText.value || '');
	}
}

watch(searchText, applyQuickFilter);
watch(isCardView, () => nextTick(applyQuickFilter));

function onExport() {
	if (!isCardView.value && gridRef.value) {
		const api = gridRef.value?.getGridApi?.();
		api.exportDataAsExcel({ fileName: 'datasharing.xlsx' });
		return;
	}
	exportRowsAsCsv();
}

function exportRowsAsCsv() {
	const measures = activeMeasures.value;
	const headers = ['EAN', t('Reference'), t('Label')];
	measures.forEach((m) => {
		headers.push(`${m.label} ${t('P1')}`, `${m.label} ${t('P2')}`, `${m.label} ${t('Gap')}`);
	});

	const lines = [headers.join(';')];
	displayedRows.value.forEach((row) => {
		const cells = [row.ean, row.reference, row.label];
		measures.forEach((m) => {
			cells.push(row[`${m.key}P1`], row[`${m.key}P2`], row[`${m.key}Evo`] == null ? '' : row[`${m.key}Evo`]);
		});
		lines.push(cells.join(';'));
	});

	const blob = new Blob(['﻿' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
	const url = URL.createObjectURL(blob);
	const link = document.createElement('a');
	link.href = url;
	link.download = 'datasharing.csv';
	link.click();
	URL.revokeObjectURL(url);
}

function handleContextChange(event) {
	const dlId = event?.detail?.deliveryLocationId ?? null;
	if (dlId == null || dlId === deliveryLocationId.value) return;
	deliveryLocationId.value = dlId;
	loadData();
}

async function loadData() {
	if (deliveryLocationId.value == null) return;
	loading.value = true;
	try {
		const diffusions = await getProductDeliveryLocationDiffusions({ deliveryLocationId: deliveryLocationId.value });
		rawData.value = diffusions;
		// ecosVM has both sales + assort products already synced by app load time
		products.value = iframeDataBridge.getProducts();
		const periods = availablePeriods.value;
		if (periods.length) {
			dateRange.value = [new Date(periods[0]), new Date(periods[periods.length - 1])];
		}
	} catch (error) {
		log('error', 'DataSharing', `Failed to load datasharing: ${error.message}`);
		rawData.value = [];
	} finally {
		loading.value = false;
	}
}

onMounted(() => {
	window.addEventListener('resize', onResize);

	_mountTarget = zoneRef.value?.parentElement;
	_mountTarget?.addEventListener('vue:datashar-context', handleContextChange);
	if (_mountTarget?._pendingDatasharContext) {
		handleContextChange({ detail: _mountTarget._pendingDatasharContext });
	}
});

onBeforeUnmount(() => {
	window.removeEventListener('resize', onResize);
	_mountTarget?.removeEventListener('vue:datashar-context', handleContextChange);
	_mountTarget = null;
});

// --- Formatting helpers -----------------------------------------------------

function round(value, decimals) {
	const factor = Math.pow(10, decimals);
	return Math.round((Number(value) || 0) * factor) / factor;
}

function evo(current, previous) {
	const prev = Number(previous) || 0;
	const cur = Number(current) || 0;
	// No P1 baseline (0 or missing): a value appearing in P2 is a full +100% gain; nothing in P2 either = no evolution.
	if (prev === 0) return cur > 0 ? 100 : null;
	return round(((cur - prev) / prev) * 100, 1);
}

function formatNumber(value, decimals) {
	return round(value, decimals).toLocaleString();
}

function formatMeasure(value, decimals, suffix) {
	if (value == null || value === '') return '';
	return `${round(value, decimals).toLocaleString()}${suffix}`;
}

function formatPct(value) {
	if (value == null) return '';
	const sign = value > 0 ? '+' : '';
	return `${sign}${round(value, 1).toLocaleString()}%`;
}

function formatDate(dateStr) {
	if (!dateStr) return '';
	return new Date(dateStr).toLocaleDateString();
}
</script>
